import {
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@material-ui/core";
import { InstalledAppsTableRow } from ".";
import { SimpleInstalledApplicationDetail } from "../types/SimpleInstalledApplicationDetail";
import { InstalledAppsMetaData } from "../../../api/axcloud";
import { useCallback, useMemo } from "react";
import { setCurrentPage, setRowsPerPage } from "../InstalledAppSlice";
import { useTypedSelector } from "../../../app/store";
import { useDispatch } from "react-redux";
import { CustomLabelDisplayedRow } from "../../../components/CustomLabelDisplayedRows";

type Props = {
  apps: SimpleInstalledApplicationDetail[];
  meta: InstalledAppsMetaData;
  isLoading?: boolean;
};

/**
 * 連携アプリ一覧テーブルのコンポーネントです。
 */
export const InstalledAppsTable: React.VFC<Props> = ({
  apps,
  meta,
  isLoading = false,
}) => {
  const { currentPage, rowsPerPage } = useTypedSelector(
    (x) => x.installedApplication.paging
  );
  const dispatch = useDispatch();
  const handleChangePage = useCallback(
    (p: number) => {
      dispatch(setCurrentPage(p));
    },
    [dispatch]
  );
  const handleChangeRowsPerPage = useCallback(
    (rpp: string) => {
      dispatch(setRowsPerPage(Number.parseInt(rpp)));
      dispatch(setCurrentPage(0));
    },
    [dispatch]
  );

  // 現ページの連携アプリ一覧
  const currentShowApps = useMemo(() => {
    const tempApps = apps.slice(
      currentPage * rowsPerPage,
      (currentPage + 1) * rowsPerPage
    );

    // 対象の連携アプリが0 かつ 戻れるページがあるなら
    if (tempApps.length === 0 && currentPage !== 0) {
      // ページを一つ戻す
      dispatch(setCurrentPage(currentPage - 1));
    }

    return tempApps;
  }, [apps, currentPage, dispatch, rowsPerPage]);

  // ページネーションコンポーネント
  const PageNationComponent = useMemo(
    () => (
      <TablePagination
        component="div"
        labelRowsPerPage="表示件数:"
        rowsPerPage={rowsPerPage}
        labelDisplayedRows={(info) => <CustomLabelDisplayedRow {...info} />}
        rowsPerPageOptions={[
          { value: 5, label: "5" },
          { value: 10, label: "10" },
          { value: 15, label: "15" },
          { value: 20, label: "20" },
          { value: 25, label: "25" },
        ]}
        count={meta.total!}
        page={currentPage}
        backIconButtonText="前のページ"
        nextIconButtonText="次のページ"
        onChangePage={(e, page) => {
          handleChangePage(page);
        }}
        onChangeRowsPerPage={(e) => handleChangeRowsPerPage(e.target.value)}
      />
    ),
    [
      currentPage,
      handleChangePage,
      handleChangeRowsPerPage,
      meta.total,
      rowsPerPage,
    ]
  );

  return (
    <TableContainer style={{ tableLayout: "fixed", minWidth: "700px" }}>
      {PageNationComponent}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell style={{ width: "30%" }} variant="head">
              名前
            </TableCell>
            <TableCell style={{ width: "55%" }} variant="head">
              概要
            </TableCell>
            <TableCell style={{ width: "120px" }} variant="head"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {currentShowApps.map((app) => (
            <InstalledAppsTableRow
              key={app.applicationId}
              applicationId={app.applicationId}
              name={app.name}
              description={app.description}
            />
          ))}
        </TableBody>
      </Table>
      {isLoading && <LinearProgress />}
      {PageNationComponent}
    </TableContainer>
  );
};
